import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { Star, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import posthog from 'posthog-js';
import { updateUserData } from '@/api/user';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';
import { USER_TYPES, USER_NPS_TRIGGER_STATE } from 'src/enums/EUser';
import { checkNPSTriggerConditions, markNPSAsDismissed, clearNPSDismissal } from '@/utils/npsUtils';
import { SmileOutlined } from '@ant-design/icons';

export const NPSPopup: React.FC = () => {
	const queryClient = useQueryClient();
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const loggedInUser = useAppSelector((state) => state.account.userAccount);

	const triggerState = checkNPSTriggerConditions(loggedInUser);

	const [isExpanded, setIsExpanded] = useState(false);
	const [isVisible, setIsVisible] = useState(true);
	const [selectedScore, setSelectedScore] = useState<number | null>(null);
	const [isSubmitted, setIsSubmitted] = useState(false);

	const updateNPSMutation = useMutation({
		mutationFn: (score: number) => {
			return updateUserData({
				clientType: USER_TYPES.CLIENT,
				data: { nps: score },
			});
		},
		onSuccess: (_data, score) => {
			setIsSubmitted(true);
			posthog.capture('nps_submitted', { rating: score });
			clearNPSDismissal();
			setTimeout(() => {
				setIsVisible(false);
			}, 1000);

			queryClient.invalidateQueries({ queryKey: ['account'] });
		},
		onError: (error) => {
			console.error('Error updating NPS:', error);
		},
	});

	useEffect(() => {
		setIsExpanded(triggerState === USER_NPS_TRIGGER_STATE.SHOW_EXPANDED);
	}, [triggerState]);

	if (!selectedAgentId || triggerState === USER_NPS_TRIGGER_STATE.HIDE || !isVisible) {
		return null;
	}

	const handleCollapse = () => {
		markNPSAsDismissed();
		posthog.capture('nps_dismissed');
		setSelectedScore(null);
		setIsExpanded(false);
	};

	const handleToggle = () => {
		setIsExpanded(true);
	};

	const handleScoreSelect = (score: number) => {
		setSelectedScore(score);
	};

	const handleSubmit = async () => {
		if (selectedScore === null) {
			return;
		}

		await updateNPSMutation.mutateAsync(selectedScore);
	};

	const shouldShowCollapsed = triggerState === USER_NPS_TRIGGER_STATE.SHOW_COLLAPSED && isVisible;

	return (
		<div className="fixed bottom-32 right-6 z-50">
			<AnimatePresence initial={false}>
				{shouldShowCollapsed && !isExpanded && (
					<button
						onClick={handleToggle}
						className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800
                        border-none rounded-full shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/35 hover:scale-110
                        transition-all duration-300 flex items-center justify-center group translate-x-[3px] translate-y-[14px]"
					>
						<SmileOutlined className="text-white text-2xl" />
					</button>
				)}

				{isExpanded && (
					<motion.div
						initial={{ scale: 0.8, opacity: 0, y: 20 }}
						animate={{ scale: 1, opacity: 1, y: 0 }}
						exit={{ scale: 0.8, opacity: 0, y: 20 }}
						transition={{
							duration: 0.4,
							type: 'spring',
							stiffness: 300,
							damping: 15,
						}}
						className="w-[90vw] sm:w-96 max-w-sm bg-white rounded-2xl shadow-xl border border-gray-200"
					>
						<div className="px-6 py-5">
							<div className="flex justify-between mb-4">
								<div className="flex gap-2">
									<div className="flex items-center">
										<Star className="w-5 h-5 text-blue-600 fill-blue-600" />
									</div>
									<h3 className="text-base font-semibold text-gray-900 flex items-center translate-y-[4px]">
										We'd love your feedback!
									</h3>
								</div>
								<Button
									type="text"
									size="small"
									onClick={handleCollapse}
									className="h-8 w-8 p-0 hover:bg-gray-100"
									icon={<X className="w-4 h-4" />}
								/>
							</div>

							<p className="text-md text-gray-600 mb-4">
								{isSubmitted || updateNPSMutation.isPending
									? 'Thanks for the feedback!'
									: 'How likely are you to recommend Frontdesk to a friend or colleague?'}
							</p>

							{!isSubmitted && !updateNPSMutation.isPending && (
								<>
									<div className="mb-3">
										<div className="grid grid-cols-11 gap-3 mb-2">
											{Array.from({ length: 11 }, (_, i) => {
												const isSelected = selectedScore === i;
												return (
													<button
														key={i}
														onClick={() => handleScoreSelect(i)}
														className={`
                                                            h-6 w-6 rounded-md text-xs font-medium transition-all duration-150
                                                            hover:scale-105 hover:shadow-sm
                                                            ${
																															isSelected
																																? 'bg-blue-600 text-white shadow-sm'
																																: 'bg-gray-100 text-gray-600 hover:bg-blue-100'
																														}
                                                            `}
													>
														{i}
													</button>
												);
											})}
										</div>
										<div className="flex justify-between text-xs text-gray-500 px-1">
											<span>Not Likely</span>
											<span>Very Likely</span>
										</div>
									</div>

									<Button
										type="primary"
										onClick={handleSubmit}
										loading={updateNPSMutation.isPending}
										disabled={selectedScore === null}
										className="w-full mt-2"
									>
										Submit
									</Button>
								</>
							)}
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};
